import { useEffect, useRef } from 'react';
import { AppUser } from '../types';
import { reverseGeocodeRegion } from '../services/regionEnrichmentService';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../services/firebaseService';
import { useLocationGate } from '../contexts/LocationGateContext';

/**
 * One-time hook: detects the user's home county via GPS
 * and saves it to their Firestore profile.
 */
export const useHomeRegionDetection = (currentUser: AppUser | null) => {
  const attemptedRef = useRef(false);
  const { gatedGetCurrentPosition } = useLocationGate();

  useEffect(() => {
    if (!currentUser || attemptedRef.current) return;
    if (currentUser.homeCounty) return;
    attemptedRef.current = true;

    gatedGetCurrentPosition(
      async (pos) => {
        try {
          const region = await reverseGeocodeRegion({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
          if (region?.county) {
            const userRef = doc(db, 'users', currentUser.uid);
            await setDoc(userRef, {
              homeCounty: region.county,
              homeCountySlug: region.county_slug,
              homeMunicipality: region.municipality,
              homeMunicipalitySlug: region.municipality_slug,
              homeRegionDetectedAt: Date.now(),
            }, { merge: true });
          }
        } catch {
          // Non-critical — silently ignore
        }
      },
      () => { /* Geolocation denied or unavailable */ },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 }
    );
  }, [currentUser?.uid, currentUser?.homeCounty, gatedGetCurrentPosition]);
};
